import { inject, injectable } from 'inversify';
import { TYPES } from '../ioc';
import type { PlanningEntityRegistry } from './PlanningEntityRegistry';
import type { PlanningAlgorithm } from '../PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '../PlanningAlgorithm/DataTypes';
import type { AlgorithmExtension, NamedValue } from '../DataFlow/DataFlow';
import { CallbackManager } from '../Utilities/CallbackManager';
import type { CallbackSubscription } from '../Utilities/CallbackManager';
import type { PlanningEntity } from './PlanningEntity';

export type AlgorithmExtensionBuilder = (descriptor: string, planing: PlanningAlgorithm) => AlgorithmExtension;

export type PerKindConfig = Map<string, AlgorithmExtensionBuilder>;

@injectable()
export class DynamicPlanningAlgorithmPromoter {
  private readonly _registry: PlanningEntityRegistry;

  private readonly _planning: PlanningAlgorithm;

  private _perKindAlgorithmExtensions?: PerKindConfig;

  private _onEntityPromotionStateChangedFor = new Map<string, CallbackManager<(isPromoted: boolean) => void>>();

  private _onEntityPromotionStateChanged = new CallbackManager<
    (kind: string, descriptor: string, isPromoted: boolean) => void
  >();

  public constructor(
    @inject(TYPES.PlanningEntityRegistry) registry: PlanningEntityRegistry,
    @inject(TYPES.PlanningAlgorithm) planning: PlanningAlgorithm
  ) {
    this._registry = registry;
    this._planning = planning;
  }

  public init(perKindAlgorithmExtensions: PerKindConfig): void {
    this._perKindAlgorithmExtensions = perKindAlgorithmExtensions;
  }

  private _getPromotedListDescriptorForKind(kind: string): string {
    const promotedListDescriptor = `promoted.${kind}.list`;
    if (!this._planning.has(promotedListDescriptor)) {
      throw new Error('Entities of this kind are not promotable');
    }
    return promotedListDescriptor;
  }

  private _getEntity(descriptor: string): PlanningEntity {
    const entity = this._registry.getByDescriptor(descriptor);
    if (!entity) {
      throw new Error('Inexistent entity');
    }
    return entity;
  }

  private _resolvePromotedListDescriptorForEntity(descriptor: string): string {
    return this._getPromotedListDescriptorForKind(this._getEntity(descriptor).kind);
  }

  private async _getPromotedListAsync(promotedListDescriptor: string): Promise<DT.IdentifiersList> {
    const promotedList = await this._planning.getValueAsync(promotedListDescriptor, DT.IdentifiersList);
    if (!promotedList) {
      throw new Error('Logic Error: List expected to be always initialized');
    }
    return promotedList;
  }

  private async _changeEntityPromotionStateAsync(entity: PlanningEntity, makePromoted: boolean): Promise<NamedValue> {
    const promotedListDescriptor = this._getPromotedListDescriptorForKind(entity.kind);
    const promotedList = await this._getPromotedListAsync(promotedListDescriptor);
    const index = promotedList.value.findIndex((entry) => entry == entity.descriptor);
    const isPromoted = index != -1;
    if (isPromoted == makePromoted) {
      throw new Error('Logic Error: repeating action');
    }
    if (makePromoted) {
      promotedList.value.push(entity.descriptor);
    } else {
      promotedList.value.splice(index, 1);
    }
    return [promotedListDescriptor, promotedList];
  }

  public async getPromotedEntitiesListAsync(kind: string): Promise<Array<string>> {
    const promotedListDescriptor = this._getPromotedListDescriptorForKind(kind);
    const promotedList = await this._getPromotedListAsync(promotedListDescriptor);
    return promotedList.value;
  }

  public onPromotedEntitiesListChanged(kind: string, callback: (value: Array<string>) => void): CallbackSubscription {
    const promotedListDescriptor = this._getPromotedListDescriptorForKind(kind);
    return this._planning.addOnChangedCallbackFor(promotedListDescriptor, (value?: DT.IdentifiersList): void => {
      if (!value) {
        throw new Error('Logic Error: List expected to be always initialized');
      }
      callback(value.value);
    });
  }

  public async promoteEntityAsync(descriptor: string): Promise<void> {
    const entity = this._getEntity(descriptor);

    // extend algorithm
    if (!this._perKindAlgorithmExtensions) {
      throw new Error('Not initialized');
    }
    const builder = this._perKindAlgorithmExtensions.get(entity.kind);
    if (!builder) {
      throw new Error('No Algorithm Extension registered for this entity kind');
    }
    await this._planning.extendAlgorithmAsync(
      builder(descriptor, this._planning),
      this._getExtensionNameByEntityDescriptor(descriptor)
    );

    // promote
    const [name, value] = await this._changeEntityPromotionStateAsync(entity, true);
    await this._planning.setValueAsync(name, value);

    this._onEntityPromotionStateChanged.invoke(entity.kind, descriptor, true);
    this._onEntityPromotionStateChangedFor.get(descriptor)?.invoke(true);
  }

  public async discardEntityAsync(descriptor: string): Promise<void> {
    const entity = this._getEntity(descriptor);

    // remove algorithm extension
    await this._planning.dropAlgorithmExtensionAsync(this._getExtensionNameByEntityDescriptor(descriptor));

    // discard
    const [name, value] = await this._changeEntityPromotionStateAsync(entity, false);
    await this._planning.setValueAsync(name, value);

    this._onEntityPromotionStateChanged.invoke(entity.kind, descriptor, false);
    this._onEntityPromotionStateChangedFor.get(descriptor)?.invoke(false);
  }

  public async getEntityPromotionStateAsync(descriptor: string): Promise<boolean> {
    const promotedListDescriptor = this._resolvePromotedListDescriptorForEntity(descriptor);
    const promotedList = await this._getPromotedListAsync(promotedListDescriptor);
    return !!promotedList.value.find((entry) => entry == descriptor);
  }

  public onEntityPromotionStateChangedFor(
    descriptor: string,
    callback: (isPromoted: boolean) => void
  ): CallbackSubscription {
    this._resolvePromotedListDescriptorForEntity(descriptor);

    if (!this._onEntityPromotionStateChangedFor.has(descriptor)) {
      this._onEntityPromotionStateChangedFor.set(descriptor, new CallbackManager<(isPromoted: boolean) => void>());
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._onEntityPromotionStateChangedFor.get(descriptor)!.subscribe(callback);
  }

  public onEntityPromotionStateChanged(
    callback: (kind: string, descriptor: string, isPromoted: boolean) => void
  ): CallbackSubscription {
    return this._onEntityPromotionStateChanged.subscribe(callback);
  }

  private _getExtensionNameByEntityDescriptor(descriptor: string): string {
    return `${descriptor}.algoExtension`;
  }
}
